import { AfterViewInit, ChangeDetectorRef, DestroyRef, Injectable, InputSignal, Signal, WritableSignal, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, catchError, finalize, lastValueFrom, of } from 'rxjs';
import { FormGroup } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ApiService } from '../../../services/api.service';
import { DialogService, DynamicDialog, DynamicDialogRef } from 'primeng/dynamicdialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { JwtPayload, ViewOnlyType, DropdownModel } from '../../models';
import { ToastrService } from 'ngx-toastr';
import { AuthService, TokenService, ErrorHandlingService } from '../../services';

@Injectable({
  providedIn: 'root',
})
export abstract class EditComponent implements AfterViewInit {
  id!: InputSignal<string | undefined> | WritableSignal<string | undefined>;
  isLoaded = signal<boolean>(false);
  show!: Signal<boolean>;
  ref = inject(DynamicDialogRef);
  dialogService = inject(DialogService);
  instance?: DynamicDialog;
  destroyRef = inject(DestroyRef);
  errorHandlingService = inject(ErrorHandlingService);
  changeDetectorRef = inject(ChangeDetectorRef);
  tokenService = inject(TokenService);
  toastr = inject(ToastrService);
  dataList: ViewOnlyType[] = [];
  dataList$ = signal<ViewOnlyType[]>([]);
  isViewOnly = signal(true);
  jwtPayload = {} as JwtPayload;
  authService = inject(AuthService);
  isFirstLoad = true;
  primaryKey = '';

  constructor(
    public service: ApiService,
    public route: ActivatedRoute,
    public router: Router,
    public formGroup: FormGroup,
    public messageService: MessageService,
  ) {
    this.bindShow();
  }

  ngAfterViewInit(): void {
    this.getJwtPayload();
    this.updateCustomerIDIfPresent();
    this.handleDialogInstanceData();
    if (this.id()) {
      this.getViewById(this.id()!);
    }
  }

  save() {
    if (this.instance) {
      if (this.instance.data.notChild) {
        this.handleSave();
        return;
      }

      this.instance.data.parentId ? this.handleSave() : this.cancel(true);
      return;
    }
    this.handleSave();
  }

  cancel(isSave?: boolean, result?: any): void {
    if (!this.instance) {
      let path = this.getPath();
      let targetPath = path;
      if (result) {
        const id = result[this.primaryKey];
        this.isViewOnly.set(true);
        targetPath = `${path}/edit/${id}`;
        this.getViewById(id);
        this.router.navigateByUrl(targetPath);
        return;
      }
      if (window.history.length > 1) {
        window.history.back();
        return;
      }
      this.router.navigateByUrl(targetPath);
      return;
    }
    this.ref.close(isSave ? result : null);
  }

  hasPermissionEdit() {
    return this.id() ? this.tokenService.hasPermission(this.service.endPoint + 'Update') : this.tokenService.hasPermission(this.service.endPoint + 'Create');
  }

  hasPermissionDelete() {
    return this.tokenService.hasPermission(this.service.endPoint + 'Delete');
  }

  toggleEdit() {
    this.isViewOnly.set(!this.isViewOnly());
    if (this.isFirstLoad) {
      this.isLoaded.set(false);
      this.isFirstLoad = false;
      this.getById(this.id()!);
    }
  }

  getById(id: string) {
    this.service
      .getById<any>(id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((error) => this.errorHandlingService.handleError(error, 'Error retrieving data.')),
      )
      .subscribe((result) => {
        this.loadSuggestions(result).then(() => {
          this.changeDetectorRef.detectChanges();
          this.formGroup.patchValue(result);
          this.changeDetectorRef.markForCheck();
          if (result.facilityIdentifier) {
            if (this.authService.refreshMenuFacility) {
              this.authService.refreshMenuFacility(result.facilityIdentifier);
            } else {
              this.authService.setCurrentFacility(result.facilityIdentifier);
            }
          }
          this.isLoaded.set(true);
        });
      });
  }

  getViewById(id: string) {
    this.service
      .getByViewId(id)
      .pipe(
        catchError((error) => this.errorHandlingService.handleError(error, 'Error retrieving data.')),
        finalize(() => this.isLoaded.set(true)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((data) => {
        this.dataList = data;
        this.dataList$.set(this.dataList);
        this.changeDetectorRef.detectChanges();
      });
  }

  handleSave(): void {
    const { value } = this.formGroup;
    this.service
      .save(value)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((error) => this.errorHandlingService.handleError(error, 'Error saving data.')),
      )
      .subscribe((result) => {
        this.toastr.success('Saved successfully', 'Success');
        this.cancel(true, result);
      });
  }

  private getPath() {
    const path = this.router.url.split('/')[1];
    return path;
  }

  public async filterEntities(
    service: { suggest: (query: string) => Observable<any> },
    setFilteredData: (result: any) => void,
    event: AutoCompleteCompleteEvent,
    isValue?: boolean,
  ) {
    const { query } = event;
    if (!query) {
      setFilteredData([]);
    }
    let result: DropdownModel[] = await lastValueFrom(
      service.suggest(query).pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((error) => {
          this.errorHandlingService.handleError(error, 'Error retrieving data.');
          return of([]);
        }),
      ),
    );
    result = result.filter((x: DropdownModel) => x.id && x.description);
    if (isValue && result.length === 0) {
      result = [{ id: query, description: '' }];
    }
    setFilteredData(result);
  }

  public async loadSuggestions(value: any) {
    void value;
  }

  getJwtPayload() {
    this.jwtPayload = this.tokenService.getJwtPayload();
  }

  handleDialogInstanceData() {
    if (this.dialogService.dialogComponentRefMap.size > 0) {
      this.instance = this.dialogService.getInstance(this.ref);
      this.changeDetectorRef.detectChanges();
      if (this.instance && this.instance.data) {
        if (this.instance.data.id) {
          this.id = signal(this.instance.data.id);
          this.bindShow();
          this.getById(this.id()!);
        } else if (this.instance.data.row) {
          this.loadSuggestions(this.instance.data.row).then(() => {
            this.changeDetectorRef.detectChanges();
            this.formGroup.patchValue(this.instance!.data.row);
            this.changeDetectorRef.markForCheck();
          });
        }
      }
    }
  }

  updateCustomerIDIfPresent() {
    if (this.jwtPayload?.CustomerID && !this.id()) {
      const customerIdControl = this.formGroup.controls['customerID'];
      if (customerIdControl) {
        this.loadSuggestions({ ...this.formGroup.value, customerID: this.jwtPayload.CustomerID }).then(() => {
          this.changeDetectorRef.detectChanges();
          customerIdControl.patchValue(this.jwtPayload.CustomerID);
          this.changeDetectorRef.markForCheck();
        });
      }
    }
  }

  private bindShow() {
    this.show = computed<boolean>(() => {
      return this.isLoaded() || !this.id();
    });
  }
}
